import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CalendarDays, Clock, MapPin, AlertCircle, X, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

export default function EventPage() {
  const { eventId } = useParams();
  const [searchParams] = useSearchParams();
  const cancelled = searchParams.get('cancelled') === 'true';

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ customer_name: '', customer_email: '', ticket_count: 1 });
  const [submitting, setSubmitting] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/events/${eventId}`)
      .then(res => setEvent(res.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [eventId]);

  useEffect(() => {
    if (cancelled && event) setDrawerOpen(true);
  }, [cancelled, event]);

  const handleBook = async () => {
    if (!form.customer_name.trim()) { toast.error('Please enter your name'); return; }
    if (!form.customer_email.trim() || !form.customer_email.includes('@')) {
      toast.error('Please enter a valid email address'); return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/events/${eventId}/book`, {
        customer_name: form.customer_name.trim(),
        customer_email: form.customer_email.trim().toLowerCase(),
        ticket_count: form.ticket_count,
      });
      if (res.data.status === 'confirmed') {
        window.location.href = `/event/${eventId}/success?booking_id=${res.data.booking_id}&free=true`;
      } else if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const total = event ? (event.price_per_ticket * form.ticket_count).toFixed(2) : '0.00';

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4">
        <div className="text-center text-white">
          <AlertCircle className="h-12 w-12 text-white/30 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Event not found</h1>
          <p className="text-white/50">This event may have ended or the link may be incorrect.</p>
        </div>
      </div>
    );
  }

  return (
    /* Full-screen background image — no overlay on the image */
    <div
      className="min-h-screen bg-slate-900 bg-cover bg-center flex items-center justify-center py-8 px-4"
      style={event.image_url ? { backgroundImage: `url(${event.image_url})` } : {}}
    >
      {/* Centred frosted card — full width on mobile, capped on desktop */}
      <div
        className="w-full max-w-md rounded-3xl shadow-2xl overflow-hidden"
        style={{
          background: 'rgba(10, 15, 30, 0.80)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          border: '1px solid rgba(255,255,255,0.10)',
        }}
      >
        {/* Cancelled banner */}
        {cancelled && (
          <div className="bg-amber-400/20 border-b border-amber-400/20 text-amber-200 text-sm text-center py-2.5 px-4">
            Payment cancelled — try again below.
          </div>
        )}

        <div className="p-7">
          {/* Restaurant */}
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/40 mb-3">
            {event.restaurant_name}
          </p>

          {/* Title */}
          <h1 className="text-3xl font-black text-white leading-tight mb-5">
            {event.name}
          </h1>

          {/* Meta pills */}
          <div className="flex flex-col gap-2 mb-5">
            <span className="inline-flex items-center gap-2 text-white/80 text-sm">
              <CalendarDays className="h-4 w-4 text-white/40 flex-shrink-0" />
              {formatDate(event.date)}
            </span>
            <span className="inline-flex items-center gap-2 text-white/80 text-sm">
              <Clock className="h-4 w-4 text-white/40 flex-shrink-0" />
              {event.time}{event.end_time ? ` – ${event.end_time}` : ''}
            </span>
            {event.location && (
              <span className="inline-flex items-center gap-2 text-white/80 text-sm">
                <MapPin className="h-4 w-4 text-white/40 flex-shrink-0" />
                {event.location}
              </span>
            )}
          </div>

          {/* Full description — no truncation */}
          {event.description && (
            <p className="text-white/65 text-sm leading-relaxed mb-6 whitespace-pre-line">
              {event.description}
            </p>
          )}

          {/* Terms & Conditions */}
          {event.terms_and_conditions && (
            <div className="mb-6 bg-white/10 rounded-xl p-4 border border-white/20">
              <p className="text-white/90 text-xs font-semibold uppercase tracking-wide mb-2">Terms &amp; Conditions</p>
              <p className="text-white/60 text-xs leading-relaxed whitespace-pre-line">
                {event.terms_and_conditions}
              </p>
            </div>
          )}

          {/* Divider */}
          <div className="border-t border-white/10 mb-5" />

          {/* Price + CTA */}
          {event.sold_out ? (
            <div className="w-full flex items-center justify-center gap-2 bg-red-500/20 text-red-300 font-black text-base py-3.5 rounded-2xl border border-red-400/30 cursor-not-allowed">
              Sold out
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                {event.is_free ? (
                  <span className="text-emerald-400 font-black text-xl">Free entry</span>
                ) : (
                  <div>
                    <span className="text-white font-black text-xl">£{event.price_per_ticket?.toFixed(2)}</span>
                    <span className="text-white/40 text-sm ml-1.5">per ticket</span>
                  </div>
                )}
              </div>
              <button
                onClick={() => setDrawerOpen(true)}
                className="w-full flex items-center justify-center gap-2 bg-white text-slate-900 font-black text-base py-3.5 rounded-2xl hover:bg-white/90 active:scale-[0.98] transition-all shadow-lg"
              >
                {event.is_free ? 'Reserve free tickets' : 'Buy tickets'}
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Booking drawer ──────────────────────────────────────────── */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50 flex flex-col justify-end">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setDrawerOpen(false)}
          />
          <div className="relative w-full max-w-lg mx-auto">
            <div className="bg-white rounded-t-3xl shadow-2xl px-6 pt-5 pb-10">
              <div className="w-10 h-1 bg-slate-200 rounded-full mx-auto mb-5" />

              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-xl font-black text-slate-900">{event.name}</h2>
                  <p className="text-sm text-slate-400 mt-0.5">
                    {event.is_free ? 'Free entry' : `£${event.price_per_ticket?.toFixed(2)} per ticket`}
                    {' · '}{formatDate(event.date)}
                  </p>
                </div>
                <button
                  onClick={() => setDrawerOpen(false)}
                  className="text-slate-300 hover:text-slate-600 p-1 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Your name</label>
                  <input
                    type="text"
                    value={form.customer_name}
                    onChange={e => setForm(f => ({ ...f, customer_name: e.target.value }))}
                    placeholder="Full name"
                    className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900 transition"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Email address</label>
                  <input
                    type="email"
                    value={form.customer_email}
                    onChange={e => setForm(f => ({ ...f, customer_email: e.target.value }))}
                    placeholder="your@email.com"
                    className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900 transition"
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    Your QR ticket{form.ticket_count !== 1 ? 's' : ''} will be sent here
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Number of tickets</label>
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => setForm(f => ({ ...f, ticket_count: Math.max(1, f.ticket_count - 1) }))}
                      className="w-11 h-11 rounded-xl bg-slate-100 text-slate-700 font-bold text-xl hover:bg-slate-200 transition-colors flex items-center justify-center"
                    >−</button>
                    <span className="text-2xl font-black text-slate-900 w-8 text-center">{form.ticket_count}</span>
                    <button
                      onClick={() => setForm(f => ({ ...f, ticket_count: f.ticket_count + 1 }))}
                      className="w-11 h-11 rounded-xl bg-slate-100 text-slate-700 font-bold text-xl hover:bg-slate-200 transition-colors flex items-center justify-center"
                    >+</button>
                    {!event.is_free && (
                      <div className="ml-auto text-right">
                        <p className="text-xs text-slate-400">Total</p>
                        <p className="text-2xl font-black text-slate-900">£{total}</p>
                      </div>
                    )}
                  </div>
                </div>

                <button
                  onClick={handleBook}
                  disabled={submitting}
                  className="w-full mt-2 bg-slate-900 hover:bg-slate-800 active:scale-[0.98] text-white font-black text-base py-4 rounded-2xl transition-all disabled:opacity-60 shadow-lg"
                >
                  {submitting
                    ? 'Processing…'
                    : event.is_free
                    ? `Reserve ${form.ticket_count} ticket${form.ticket_count !== 1 ? 's' : ''} — Free`
                    : `Pay £${total} with Stripe`}
                </button>

                <p className="text-xs text-slate-400 text-center">
                  {event.is_free ? 'No payment needed. QR codes emailed instantly.' : 'Secure payment via Stripe. QR codes emailed after payment.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
